import React, { ClipboardEventHandler, createRef, FocusEventHandler, forwardRef, useEffect, useState } from "react";

import { bemClassNames, combineRefs, TAB_INDEX_FOCUSABLE_ORDER_EXPLICIT, useForceUpdate } from "@multiweb/reactUtils";
import { IconCode } from "@multiweb/types/contracts";
import {
    AccessibilityProps,
    ForwardRefType,
    OnJavaScriptChange,
    OptionalTabIndex,
    TextAlign,
    TextDirectionality,
} from "@multiweb/types/react";
import { ButtonBackgroundColor } from "../ButtonProvider";
import { EncryptedSymbol } from "../EncryptedSymbol";
import { IconColor } from "../Icon";
import { IconButton } from "../IconButton";
import { Input, InputType } from "../Input";
import { SpacingSize } from "../Layout";
import { FormComponentEventHandlersProps, FormComponentProps } from "../types";
import { generateFormComponentClasses } from "../utils";
import { EditComponentInputType } from "./types";

const classNames = bemClassNames("edit-component");

export type AdornmentIconOptions = {
    icon?: IconCode;
    iconColor?: IconColor;
    tooltipText?: string;
    onClick: () => void;
};

export type EditComponentProps = {
    /** Jazyk vstupní hodnoty */
    lang?: string;
    /** Maximální počet vložených znaků */
    maxLength?: number;
    /** Minimální počet vložených znaků */
    minLength?: number;
    /** Callback volaný při změně hodnoty pole */
    onChange?: (value: string | undefined) => void;
    /** Text zobrazovaný při prázdné hodnotě vstupu */
    placeholder?: string;
    /** Příznak, zda se má kontrolovat pravopis */
    spellCheck?: boolean;
    /** Regulární výraz, podle kterého je hodnota pole kontrolována při odeslání formuláře */
    pattern?: string;
    /** Hodnota pole */
    value: string | undefined;
    /** Typ vstupního pole. */
    type?: EditComponentInputType;
    /** Umožňuje nastavit tabindex */
    tabIndex?: OptionalTabIndex | typeof TAB_INDEX_FOCUSABLE_ORDER_EXPLICIT;
    /** Callback volaný při odposlechnutí javascriptové `change` eventy */
    onJavaScriptChange?: OnJavaScriptChange;
    /** Určuje směr textu */
    dir?: TextDirectionality;
    /** Zarovnání textu */
    textAlign?: TextAlign;
    /** Příznak, zda se má upravovat pozice kurzoru */
    fixCaretPosition?: boolean;
    /** Vysvětlivka pole */
    tooltipText?: string;
    /** Možnosti elementu zobrazujícího se na konci inputu. */
    endAdornment?: AdornmentIconOptions;
    /** Callback volaný při kopírování do clipboardu z inputu. */
    onCopy?: ClipboardEventHandler<HTMLInputElement>;
    /** Callback volaný při vložení z clipboardu do inputu. */
    onPaste?: ClipboardEventHandler<HTMLInputElement>;
} & FormComponentProps & FormComponentEventHandlersProps & Pick<AccessibilityProps, "ariaDescribedBy">;

/** Interní komponenta řešící stylování, formátování hodnoty pole a manipulaci s callbacky */
export const EditComponent: ForwardRefType<HTMLInputElement, EditComponentProps> = forwardRef<HTMLInputElement, EditComponentProps>(({
    id,
    disabled = false,
    encrypted = false,
    invalid = false,
    lang,
    maxLength,
    minLength,
    onBlur,
    onChange: onChangeExternal,
    onFocus: onFocusExternal,
    onClick,
    onMouseOver,
    onMouseEnter,
    onMouseLeave,
    onCopy,
    onPaste,
    placeholder,
    backgroundColor,
    required = false,
    spellCheck,
    pattern,
    value = "",
    tabIndex,
    type = InputType.text,
    onJavaScriptChange,
    readOnly = true,
    transparent = false,
    dir,
    textAlign,
    fixCaretPosition = true,
    ariaDescribedBy,
    tooltipText,
    endAdornment,
}, ref) => {
    let classes;
    if (transparent) {
        classes = classNames({ transparent });
    } else {
        classes = generateFormComponentClasses(classNames, {
            disabled,
            encrypted,
            invalid,
            required,
            backgroundColor,
            readOnly,
        }, [classNames({ transparent })]);
    }

    const innerRef = createRef<HTMLInputElement>();
    const joinedRef = combineRefs<HTMLInputElement>(innerRef, ref);

    const [lastOnChangeValue, setLastOnChangeValue] = useState<string>("");
    const [startIndex, setStartIndex] = useState<number>(0);
    const [endIndex, setEndIndex] = useState<number>(0);

    const forceUpdate = useForceUpdate();

    useEffect(() => {
        if (value && innerRef.current && fixCaretPosition) {
            if (value.length === lastOnChangeValue.length) {
                /* Pro pripady validni hodnoty editu a pro pripady, kdy se nahrazuje znak za jiny znak. Napr. "abcde" za "aZcde". */
                innerRef.current.setSelectionRange(startIndex, endIndex);
            } else {
                /* Procesovani nevalidnich hodnot */
                if (startIndex === 0) {
                    /* Pro pripady, kdy je hodnota nevalidni a mazeme prvni znak z retezce. */
                    innerRef.current.setSelectionRange(0, 0);
                } else {
                    /* Pro pripady, kdy je hodnota retezce nevalidni a zaroven nepracujeme s prvnim znakem retezce.
                    * Napr. pokud zadame maxLength na 3 a nove chceme "abc" prepsat na "abcd", tak takovy retezec je nevalidni a zpet nam jako value prijde namisto "abcd" retezec "abc".
                    *       Proto je nutne vratit se o pozici zpet.
                    */
                    innerRef.current.setSelectionRange(startIndex - 1, endIndex - 1);
                }
            }
        }
    /** Nutnost předání všech použitých referencí byla vypnuta kvůli nechtěnému vyvolání useEffect při změně: value, fixCaretPosition, innerRef */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startIndex, endIndex]);

    const onChange = (val: string | undefined) => {
        handleCaretPosition();
        onChangeExternal && onChangeExternal(val);
    };

    const handleCaretPosition = () => {
        if (innerRef.current) {
            const el = innerRef.current;

            if (el.selectionStart && el.selectionEnd) {
                setStartIndex(el.selectionStart);
                setEndIndex(el.selectionEnd);

                /* pokud se stav nemění (tzn. nemeni se hodnota), je nutné re-render vyvolat ručně */
                if (el.value === lastOnChangeValue) {
                    forceUpdate();
                }

                setLastOnChangeValue(el.value);
            } else {
                setStartIndex(0);
                setEndIndex(0);
            }
        }
    };

    const onFocus: FocusEventHandler = (e) => {
        setStartIndex(value?.length);
        setEndIndex(value?.length);
        setLastOnChangeValue(value);

        onFocusExternal && onFocusExternal(e);
    };

    return (
        <div className={classes}>
            {encrypted && <EncryptedSymbol />}
            <Input
                {...{
                    id,
                    required,
                    spellCheck,
                    pattern,
                    placeholder,
                    maxLength,
                    minLength,
                    lang,
                    tabIndex,
                    onChange,
                    onJavaScriptChange,
                    value,
                    type,
                    onBlur,
                    onClick,
                    onMouseOver,
                    onMouseEnter,
                    onMouseLeave,
                    onFocus,
                    ref: joinedRef,
                    readOnly,
                    dir,
                    textAlign,
                    onCopy,
                    onPaste,
                }}
                title={tooltipText}
                disabled={disabled || encrypted}
                hidden={encrypted}
                aria-invalid={invalid}
                aria-describedby={ariaDescribedBy}
            />
            {(endAdornment && value) &&
                <div className={classNames("end-adornment-wrapper")}>
                    <IconButton
                        spacingSize={SpacingSize.none}
                        backgroundColor={ButtonBackgroundColor.transparent}
                        disabled={disabled || encrypted}
                        iconCode={endAdornment.icon || IconCode.uiCrossSmall}
                        onClick={endAdornment.onClick}
                        color={endAdornment.iconColor || IconColor.blue}
                    />
                </div>
            }
        </div>
    );
});
